import type { DocNode } from '@atlaskit/adf-schema';
import type { ConfluenceUpdatePageCommandProperties } from '@atlassian/rovo-content-bridge-api-commands/commands/confluence-update-page-command';

import type { PassThroughCommandProps } from './command-handler';
import type { PageUpdateStrategy } from './page-update-strategy';
import { StreamingCommandHandler } from './streaming-command-handler';

/**
 * Dispatches streaming chunks to the strategy matching `payload.key`.
 * Multiple strategies can be active within a single streaming session (e.g. an
 * ADF update interleaved with a dynamic-UI update). Only strategies that actually
 * received chunks are finished/aborted.
 */
export class PageUpdateCommandHandler extends StreamingCommandHandler<ConfluenceUpdatePageCommandProperties> {
	private activeStrategies = new Set<PageUpdateStrategy>();

	constructor(
		props: PassThroughCommandProps,
		private strategies: Map<string, PageUpdateStrategy>,
	) {
		super(props, 'update');
	}

	async process(payload: ConfluenceUpdatePageCommandProperties): Promise<void> {
		const strategy = this.strategies.get(payload.key);
		if (strategy) {
			this.activeStrategies.add(strategy);
			await strategy.process(payload);
		}
	}

	async finishAction(): Promise<DocNode | undefined> {
		const results = await Promise.all(Array.from(this.activeStrategies, (s) => s.finish?.()));
		this.activeStrategies.clear();
		return results.find((r) => r !== undefined);
	}

	abort(reason?: 'aborted' | 'timeout', options?: { suppressError?: boolean }): void {
		this.activeStrategies.forEach((s) => s.abort?.(reason, options));
		this.activeStrategies.clear();
	}
}
